import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { AlertTriangleIcon, BoxesIcon, CircleDollarSignIcon, ClipboardListIcon, PackageCheckIcon, PackageXIcon, PillIcon, ShieldCheckIcon, TriangleAlertIcon } from 'lucide-react';
import { PharmacySection } from './PharmacyModule';
import { expiryAlertWindow, formatCurrency, isLowStockOnly, isOutOfStock, isToday } from './pharmacyUtils';
type Props = {
  onNavigate: (section: PharmacySection) => void;
};
export function PharmacyOverview({
  onNavigate
}: Props) {
  const {
    currentUser,
    medicines,
    medicineBatches,
    purchaseOrders,
    stockMovements,
    encounters,
    pharmacySettings,
    updatePharmacySettings
  } = useData();
  const [savingPolicy, setSavingPolicy] = useState(false);
  const manager = currentUser?.role === 'Pharmacy Manager';
  const stats = useMemo(() => {
    const expiry = (window: Exclude<ReturnType<typeof expiryAlertWindow>, null>) => medicineBatches.filter(batch => expiryAlertWindow(batch) === window);
    return {
      lowStock: medicines.filter(isLowStockOnly),
      outOfStock: medicines.filter(isOutOfStock),
      expired: expiry('Expired'),
      days30: expiry('30 Days'),
      days60: expiry('60 Days'),
      days90: expiry('90 Days'),
      todayDispensed: stockMovements.filter(movement => movement.type === 'Dispense' && isToday(movement.createdAt)),
      todayReceived: stockMovements.filter(movement => movement.type === 'Receipt' && isToday(movement.createdAt)),
      pendingOrders: purchaseOrders.filter(order => order.status === 'Pending').length,
      pendingPrescriptions: encounters.filter(encounter => encounter.stage === 'PHARMACY').length,
      value: medicines.reduce((sum, medicine) => sum + medicine.currentQuantity * medicine.costPrice, 0)
    };
  }, [encounters, medicineBatches, medicines, purchaseOrders, stockMovements]);
  const updatePolicy = async (allowMedicineReturns: boolean) => {
    setSavingPolicy(true);
    await updatePharmacySettings({
      allowMedicineReturns
    });
    setSavingPolicy(false);
  };
  const cards = [{
    label: 'Total medicines',
    value: medicines.length,
    icon: PillIcon,
    tone: 'blue'
  }, {
    label: 'Inventory value',
    value: formatCurrency(stats.value),
    icon: CircleDollarSignIcon,
    tone: 'emerald'
  }, {
    label: 'Low stock',
    value: stats.lowStock.length,
    icon: AlertTriangleIcon,
    tone: 'amber'
  }, {
    label: 'Out of stock',
    value: stats.outOfStock.length,
    icon: PackageXIcon,
    tone: 'red'
  }, {
    label: 'Expired batches',
    value: stats.expired.length,
    icon: TriangleAlertIcon,
    tone: 'red'
  }, {
    label: 'Dispensed today',
    value: stats.todayDispensed.length,
    icon: PackageCheckIcon,
    tone: 'teal'
  }, {
    label: 'Received today',
    value: stats.todayReceived.length,
    icon: BoxesIcon,
    tone: 'blue'
  }] as const;
  return <div className="space-y-6" data-id="element-4879">
      <section className="flex flex-col gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-4880">
        <div data-id="element-4881">
          <p className="text-sm font-semibold text-pink-600" data-id="element-4882">
            BMED Care pharmacy desk
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl" data-id="element-4883">
            Inventory with every dispense accounted for.
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600" data-id="element-4884">
            Live stock levels, batch controls, policy enforcement, and patient
            dispensing in one operational view.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-4885">
          <button type="button" onClick={() => onNavigate('receive-stock')} className="inline-flex items-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700" data-id="element-4886">
            <PackageCheckIcon className="mr-1.5 h-4 w-4" data-id="element-4887" /> Receive stock
          </button>
          <button type="button" onClick={() => onNavigate('dispensing')} className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-id="element-4888">
            <ClipboardListIcon className="mr-1.5 h-4 w-4" data-id="element-4889" /> Dispensing queue
          </button>
        </div>
      </section>
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-4890">
        {cards.map(({
        label,
        value,
        icon: Icon,
        tone
      }) => <article key={label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4891">
            <div className="flex items-start justify-between gap-3" data-id="element-4892">
              <div data-id="element-4893">
                <p className="text-sm font-medium text-slate-500" data-id="element-4894">{label}</p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-slate-950" data-id="element-4895">
                  {value}
                </p>
              </div>
              <div className={`rounded-lg p-2.5 ${iconTone(tone)}`} data-id="element-4896">
                <Icon className="h-5 w-5" data-id="element-4897" />
              </div>
            </div>
          </article>)}
      </section>
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.35fr_0.85fr]" data-id="element-4898">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4899">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-4900">
            <div data-id="element-4901">
              <h3 className="font-bold text-slate-900" data-id="element-4902">
                Automated operational alerts
              </h3>
              <p className="mt-0.5 text-sm text-slate-500" data-id="element-4903">
                Counts update as soon as a stock transaction commits.
              </p>
            </div>
            <button type="button" onClick={() => onNavigate('low-stock')} className="text-sm font-semibold text-blue-600 hover:text-blue-700" data-id="element-4904">
              Review stock alerts
            </button>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-4905">
            <AlertRow label="Out of stock" count={stats.outOfStock.length} tone="red" onClick={() => onNavigate('low-stock')} data-id="element-4906" />
            <AlertRow label="At or below reorder level" count={stats.lowStock.length} tone="amber" onClick={() => onNavigate('low-stock')} data-id="element-4907" />
            <AlertRow label="Expired — dispensing blocked" count={stats.expired.length} tone="red" onClick={() => onNavigate('expired')} data-id="element-4908" />
            <AlertRow label="Expiry alerts · 30 / 60 / 90 days" count={stats.days30.length + stats.days60.length + stats.days90.length} tone="amber" detail={`${stats.days30.length} at 30d · ${stats.days60.length} at 60d · ${stats.days90.length} at 90d`} onClick={() => onNavigate('expired')} data-id="element-4909" />
          </div>
        </div>
        <aside className="space-y-5" data-id="element-4910">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4911">
            <div className="flex items-center gap-3" data-id="element-4912">
              <span className="rounded-lg bg-teal-50 p-2 text-teal-700" data-id="element-4913">
                <ShieldCheckIcon className="h-5 w-5" data-id="element-4914" />
              </span>
              <div data-id="element-4915">
                <h3 className="font-bold text-slate-900" data-id="element-4916">
                  Medicine return policy
                </h3>
                <p className="text-sm text-slate-500" data-id="element-4917">
                  Last updated by {pharmacySettings.updatedByName}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 p-3" data-id="element-4918">
              <span className="text-sm font-medium text-slate-700" data-id="element-4919">
                Allow medicine returns
              </span>
              <button type="button" role="switch" aria-checked={pharmacySettings.allowMedicineReturns} disabled={!manager || savingPolicy} onClick={() => updatePolicy(!pharmacySettings.allowMedicineReturns)} className={`relative h-6 w-11 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${pharmacySettings.allowMedicineReturns ? 'bg-teal-600' : 'bg-slate-300'}`} data-id="element-4920">
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${pharmacySettings.allowMedicineReturns ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-4921" />
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500" data-id="element-4922">
              {manager ? 'Only Pharmacy Managers can change this hospital policy.' : pharmacySettings.allowMedicineReturns ? 'Returns are enabled by the current hospital policy.' : 'Returns are disabled by the current hospital policy.'}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4923">
            <h3 className="font-bold text-slate-900" data-id="element-4924">Today’s desk</h3>
            <div className="mt-4 space-y-3" data-id="element-4925">
              <DeskMetric label="Prescriptions waiting" value={stats.pendingPrescriptions} onClick={() => onNavigate('dispensing')} data-id="element-4926" />
              <DeskMetric label="Pending purchase orders" value={stats.pendingOrders} onClick={() => onNavigate('purchase-orders')} data-id="element-4927" />
              <DeskMetric label="Stock received today" value={stats.todayReceived.length} onClick={() => onNavigate('movement-history')} data-id="element-4928" />
            </div>
          </div>
        </aside>
      </section>
    </div>;
}
function AlertRow({
  label,
  count,
  tone,
  detail,
  onClick
}: {
  label: string;
  count: number;
  tone: 'red' | 'amber';
  detail?: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-slate-50" data-id="element-4929">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone === 'red' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`} data-id="element-4930">
        <AlertTriangleIcon className="h-4 w-4" data-id="element-4931" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-4932">
        <span className="block text-sm font-semibold text-slate-900" data-id="element-4933">
          {label}
        </span>
        <span className="mt-0.5 block text-sm text-slate-500" data-id="element-4934">
          {detail || 'Live inventory control indicator'}
        </span>
      </span>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${tone === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-4935">
        {count}
      </span>
    </button>;
}
function DeskMetric({
  label,
  value,
  onClick
}: {
  label: string;
  value: number;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3 text-left hover:border-blue-200 hover:bg-blue-50/40" data-id="element-4936">
      <span className="text-sm font-medium text-slate-700" data-id="element-4937">{label}</span>
      <span className="text-xl font-bold text-slate-950" data-id="element-4938">{value}</span>
    </button>;
}
function iconTone(tone: string) {
  return {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    teal: 'bg-teal-50 text-teal-700'
  }[tone] || 'bg-slate-100 text-slate-700';
}